import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { Order, OrderStatus } from '../types';
import {
  formatEuro,
  sanitizeWhatsAppPhone
} from '../utils/format';
import {
  Volume2,
  VolumeX,
  Clock,
  CheckCircle2,
  Flame,
  Bell,
  Utensils,
  MessageSquare,
  Check,
  ChefHat,
  Sparkles,
  Edit3
} from 'lucide-react';
import { EditOrderModal } from './EditOrderModal';

export const StaffDashboard: React.FC = () => {
  const {
    allOrders,
    updateOrderStatus,
    reviewOrderChange,
    soundEnabled,
    setSoundEnabled,
    currentStaffRole
  } = useApp();

  const isKitchenOnly = currentStaffRole === 'kitchen';
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);

  // Group orders
  const submittedOrders = allOrders.filter(o => o.status === 'submitted');
  const confirmedOrders = allOrders.filter(o => o.status === 'confirmed');
  const preparingOrders = allOrders.filter(o => o.status === 'preparing');
  const readyOrders = allOrders.filter(o => o.status === 'ready');

  // Change requests needing review
  const pendingChangeOrders = allOrders.filter(o => o.change_status === 'requested');

  const getElapsedMinutes = (dateStr: string) => {
    const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    return mins <= 0 ? 'Just now' : `${mins} min ago`;
  };

  const getCustomerRepeatInfo = (phone?: string) => {
    if (!phone) return null;
    const matchOrders = allOrders.filter(o => o.customer_phone === phone || o.customer_id === phone);
    if (matchOrders.length > 1) {
      const totalSpent = matchOrders.reduce((sum, o) => sum + (Number(o.total_amount) || 0), 0);
      return { count: matchOrders.length, totalSpent };
    }
    return null;
  };

  return (
    <div className="staff-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Top Staff Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          background: 'var(--bg-secondary)',
          padding: '16px 20px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: 'var(--radius-md)',
              background: isKitchenOnly
                ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                : 'linear-gradient(135deg, #e05a36, #c2410c)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isKitchenOnly ? <ChefHat size={22} /> : <Flame size={22} />}
          </div>
          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 800 }}>
              {isKitchenOnly ? 'Kitchen Display System (KDS)' : 'Live Orders & Kitchen Board'}
            </h2>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {isKitchenOnly
                ? 'Live Cooking Queue • Dish Preparation & Customizations'
                : 'Real-time Wood-Fired Oven & Dining Room Monitor'}
            </div>
          </div>
        </div>

        {/* Global Sound Tone Quick Toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            className={soundEnabled ? 'btn-primary' : 'btn-secondary'}
            style={{ padding: '8px 16px', fontSize: '0.85rem' }}
            onClick={() => setSoundEnabled(!soundEnabled)}
          >
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            <span>{soundEnabled ? 'Kitchen Audio Chimes ON' : 'Muted'}</span>
          </button>
        </div>
      </div>

      {editingOrder && (
        <EditOrderModal 
          order={editingOrder}
          isOpen={Boolean(editingOrder)}
          onClose={() => setEditingOrder(null)} 
        />
      )}

      {/* 4-Column Live Kanban Board */}
      <div className="kanban-board">
        {/* Column 1: New / Submitted */}
        <div className="kanban-column">
          <div className="kanban-column-header" style={{ borderTop: '4px solid var(--accent-amber)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={18} color="var(--accent-amber)" />
              <span>1. New Orders</span>
            </div>
            <span style={{ background: 'var(--bg-surface)', padding: '2px 8px', borderRadius: '12px', fontSize: '0.85rem' }}>
              {submittedOrders.length}
            </span>
          </div>

          <div className="kanban-column-body">
            {submittedOrders.map(order => (
              <div key={order.id} className="order-kanban-card urgent">
                <div className="order-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span className="table-badge-large">Table #{order.table_number}</span>
                    <span
                      style={{
                        fontSize: '0.74rem',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-subtle)',
                        padding: '1px 5px',
                        borderRadius: 'var(--radius-xs)',
                        fontWeight: 700,
                        color: 'var(--text-primary)'
                      }}
                      title="Diners at Table"
                    >
                      👥 {order.guest_count || 2}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {getElapsedMinutes(order.created_at)}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700 }}>
                    {order.customer_name} ({order.order_number})
                  </div>
                  {(() => {
                    const r = getCustomerRepeatInfo(order.customer_phone);
                    if (r) {
                      return (
                        <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--accent-copper)', background: 'rgba(224, 90, 54, 0.12)', padding: '2px 6px', borderRadius: 'var(--radius-full)' }}>
                          🔁 Repeat ({r.count}x)
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Items (Clean view for kitchen without prices) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid var(--border-subtle)', paddingTop: '8px' }}>
                  {order.items.map(it => (
                    <div key={it.id} style={{ fontSize: '0.88rem' }}>
                      <strong style={{ color: 'var(--accent-amber)' }}>{it.quantity}x</strong> {it.name}
                      {it.selected_variants && it.selected_variants.length > 0 && (
                        <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginLeft: '16px' }}>
                          {it.selected_variants.map(v => v.option_name).join(', ')}
                        </div>
                      )}
                      {it.special_instructions && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--accent-amber)', fontStyle: 'italic', marginLeft: '16px' }}>
                          "{it.special_instructions}"
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Order value ONLY visible to Manager/Admin, strictly hidden for Kitchen */}
                {!isKitchenOnly && (
                  <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--accent-copper)', marginTop: '4px' }}>
                    Total: {formatEuro(order.total_amount)}
                  </div>
                )}

                {/* Action: Confirm */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  {!isKitchenOnly && (
                    <button
                      className="btn-secondary"
                      style={{ padding: '8px 12px' }}
                      title="Edit Order Items & Notes"
                      onClick={() => setEditingOrder(order)}
                    >
                      <Edit3 size={16} color="var(--accent-copper)" />
                    </button>
                  )}
                  <button
                    className="btn-primary"
                    style={{ flex: 1, padding: '8px' }}
                    onClick={() => updateOrderStatus(order.id, 'confirmed')}
                  >
                    <CheckCircle2 size={16} />
                    <span>Accept Order</span>
                  </button>
                </div>
              </div>
            ))}
            {submittedOrders.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                No new pending orders
              </div>
            )}
          </div>
        </div>

        {/* Column 2: Confirmed / Prepping */}
        <div className="kanban-column">
          <div className="kanban-column-header" style={{ borderTop: '4px solid #38bdf8' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ChefHat size={18} color="#38bdf8" />
              <span>2. Prepping</span>
            </div>
            <span style={{ background: 'var(--bg-surface)', padding: '2px 8px', borderRadius: '12px', fontSize: '0.85rem' }}>
              {confirmedOrders.length}
            </span>
          </div>

          <div className="kanban-column-body">
            {confirmedOrders.map(order => (
              <div key={order.id} className="order-kanban-card">
                <div className="order-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span className="table-badge-large">Table #{order.table_number}</span>
                    <span
                      style={{
                        fontSize: '0.74rem',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-subtle)',
                        padding: '1px 5px',
                        borderRadius: 'var(--radius-xs)',
                        fontWeight: 700,
                        color: 'var(--text-primary)'
                      }}
                      title="Diners at Table"
                    >
                      👥 {order.guest_count || 2}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {getElapsedMinutes(order.created_at)}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700 }}>
                    {order.customer_name} ({order.order_number})
                  </div>
                  {(() => {
                    const r = getCustomerRepeatInfo(order.customer_phone);
                    if (r) {
                      return (
                        <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--accent-copper)', background: 'rgba(224, 90, 54, 0.12)', padding: '2px 6px', borderRadius: 'var(--radius-full)' }}>
                          🔁 Repeat ({r.count}x)
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid var(--border-subtle)', paddingTop: '8px' }}>
                  {order.items.map(it => (
                    <div key={it.id} style={{ fontSize: '0.88rem' }}>
                      <strong style={{ color: '#38bdf8' }}>{it.quantity}x</strong> {it.name}
                      {it.selected_variants && it.selected_variants.length > 0 && (
                        <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginLeft: '16px' }}>
                          {it.selected_variants.map(v => v.option_name).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {!isKitchenOnly && (
                  <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--accent-copper)', marginTop: '4px' }}>
                    Total: {formatEuro(order.total_amount)}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  {!isKitchenOnly && (
                    <button
                      className="btn-secondary"
                      style={{ padding: '8px 12px' }}
                      title="Edit Order Items & Notes"
                      onClick={() => setEditingOrder(order)}
                    >
                      <Edit3 size={16} color="var(--accent-copper)" />
                    </button>
                  )}
                  <button
                    className="btn-primary"
                    style={{ flex: 1, padding: '8px', background: 'var(--accent-copper)' }}
                    onClick={() => updateOrderStatus(order.id, 'preparing')}
                  >
                    <Flame size={16} />
                    <span>Send to Oven</span>
                  </button>
                </div>
              </div>
            ))}
            {confirmedOrders.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                No orders in prep
              </div>
            )}
          </div>
        </div>

        {/* Column 3: In Wood-Fired Oven */}
        <div className="kanban-column">
          <div className="kanban-column-header" style={{ borderTop: '4px solid var(--accent-copper)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Flame size={18} color="var(--accent-copper)" />
              <span>3. In Wood-Fired Oven</span>
            </div>
            <span style={{ background: 'var(--bg-surface)', padding: '2px 8px', borderRadius: '12px', fontSize: '0.85rem' }}>
              {preparingOrders.length}
            </span>
          </div>

          <div className="kanban-column-body">
            {preparingOrders.map(order => (
              <div key={order.id} className="order-kanban-card">
                <div className="order-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span className="table-badge-large">Table #{order.table_number}</span>
                    <span
                      style={{
                        fontSize: '0.74rem',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-subtle)',
                        padding: '1px 5px',
                        borderRadius: 'var(--radius-xs)',
                        fontWeight: 700,
                        color: 'var(--text-primary)'
                      }}
                      title="Diners at Table"
                    >
                      👥 {order.guest_count || 2}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {getElapsedMinutes(order.created_at)}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700 }}>
                    {order.customer_name} ({order.order_number})
                  </div>
                  {(() => {
                    const r = getCustomerRepeatInfo(order.customer_phone);
                    if (r) {
                      return (
                        <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--accent-copper)', background: 'rgba(224, 90, 54, 0.12)', padding: '2px 6px', borderRadius: 'var(--radius-full)' }}>
                          🔁 Repeat ({r.count}x)
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid var(--border-subtle)', paddingTop: '8px' }}>
                  {order.items.map(it => (
                    <div key={it.id} style={{ fontSize: '0.88rem' }}>
                      <strong style={{ color: 'var(--accent-copper)' }}>{it.quantity}x</strong> {it.name}
                      {it.selected_variants && it.selected_variants.length > 0 && (
                        <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginLeft: '16px' }}>
                          {it.selected_variants.map(v => v.option_name).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {!isKitchenOnly && (
                  <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--accent-copper)', marginTop: '4px' }}>
                    Total: {formatEuro(order.total_amount)}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  {!isKitchenOnly && (
                    <button
                      className="btn-secondary"
                      style={{ padding: '8px 12px' }}
                      title="Edit Order Items & Notes"
                      onClick={() => setEditingOrder(order)}
                    >
                      <Edit3 size={16} color="var(--accent-copper)" />
                    </button>
                  )}
                  <button
                    className="btn-primary"
                    style={{ flex: 1, padding: '8px', background: 'var(--accent-emerald)' }}
                    onClick={() => updateOrderStatus(order.id, 'ready')}
                  >
                    <Bell size={16} />
                    <span>Food Ready</span>
                  </button>
                </div>
              </div>
            ))}
            {preparingOrders.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Oven is currently clear
              </div>
            )}
          </div>
        </div>

        {/* Column 4: Ready to Serve */}
        <div className="kanban-column">
          <div className="kanban-column-header" style={{ borderTop: '4px solid var(--accent-emerald)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Utensils size={18} color="var(--accent-emerald)" />
              <span>4. Ready to Serve</span>
            </div>
            <span style={{ background: 'var(--bg-surface)', padding: '2px 8px', borderRadius: '12px', fontSize: '0.85rem' }}>
              {readyOrders.length}
            </span>
          </div>

          <div className="kanban-column-body">
            {readyOrders.map(order => (
              <div key={order.id} className="order-kanban-card">
                <div className="order-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span className="table-badge-large">Table #{order.table_number}</span>
                    <span
                      style={{
                        fontSize: '0.74rem',
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-subtle)',
                        padding: '1px 5px',
                        borderRadius: 'var(--radius-xs)',
                        fontWeight: 700,
                        color: 'var(--text-primary)'
                      }}
                      title="Diners at Table"
                    >
                      👥 {order.guest_count || 2}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {getElapsedMinutes(order.created_at)}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700 }}>
                    {order.customer_name} ({order.order_number})
                  </div>
                  {(() => {
                    const r = getCustomerRepeatInfo(order.customer_phone);
                    if (r) {
                      return (
                        <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--accent-copper)', background: 'rgba(224, 90, 54, 0.12)', padding: '2px 6px', borderRadius: 'var(--radius-full)' }}>
                          🔁 Repeat ({r.count}x)
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid var(--border-subtle)', paddingTop: '8px' }}>
                  {order.items.map(it => (
                    <div key={it.id} style={{ fontSize: '0.88rem' }}>
                      <strong style={{ color: 'var(--accent-emerald)' }}>{it.quantity}x</strong> {it.name}
                    </div>
                  ))}
                </div>

                {!isKitchenOnly && (
                  <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--accent-copper)', marginTop: '4px' }}>
                    Total: {formatEuro(order.total_amount)}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  {!isKitchenOnly && order.customer_phone && (
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ padding: '8px 12px', color: '#25D366', borderColor: '#25D366' }}
                      title="Send WhatsApp Satisfaction Feedback Survey"
                      onClick={() => {
                        const phone = sanitizeWhatsAppPhone(order.customer_phone);
                        const msg = `Hei ${order.customer_name || 'asiakkaamme'}! 👋🍕\n\nKiitos käynnistäsi The Oven Storyssa (Pöytä #${order.table_number})!\nToivottavasti ruokailukokemuksesi oli erinomainen ja pizzamme maistuivat. Voit antaa meille palautetta vastaamalla suoraan tähän viestiin tai Google Reviewsissä: theovenstory.fi/feedback\n\nLämpimästi tervetuloa uudelleen! ✨\n- The Oven Story`;
                        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
                      }}
                    >
                      <MessageSquare size={16} />
                    </button>
                  )}
                  <button
                    className="btn-primary"
                    style={{ flex: 1, padding: '8px', background: 'var(--accent-emerald)' }}
                    onClick={() => updateOrderStatus(order.id, 'completed')}
                  >
                    <Check size={16} />
                    <span>Served • Completed</span>
                  </button>
                </div>
              </div>
            ))}
            {readyOrders.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                All dishes served
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
